import { useEffect, useState } from 'react';
import { CheckCircle2, Copy, FileText, Loader2, Send, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, downloadAuthedFile, formatEur, inputCls, requestStatusLabel, selectCls, statusBadge } from '../../utils/operations';
import { AdminProposalCalculator } from './AdminProposalCalculator';

const STATUSES = ['draft', 'request_sent', 'waiting_partner', 'available', 'alternative_proposed', 'awaiting_customer', 'payment_pending', 'confirmed', 'completed', 'cancelled'];

const emptyProposal = {
  price_eur: '', margin_pct: '', services: '', message: '', valid_until: '',
  travel_dates: '', travellers: '', customer_currency: 'EUR', deposit_pct: '', payment_deadline: '',
  itinerary: '', experiences: '', transfers: '', included: '', not_included: '',
  acc_property: '', acc_room: '', acc_nights: '', acc_meal: '',
  cond_cancellation: '', cond_modification: '', cond_important: '',
};

const parseLines = (text, parts) =>
  text.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
    const cols = line.split('|').map((c) => c.trim());
    return Object.fromEntries(parts.map((k, i) => [k, cols[i] || '']));
  });

const ITEM_STATUSES = ['pending', 'quoted', 'confirmed', 'unavailable', 'removed'];

const CartItemRow = ({ request, item, partners, onRefresh }) => {
  const [form, setForm] = useState({
    status: item.status || 'pending',
    item_partner_name: item.item_partner_name || '',
    price_net_eur: item.price_net_eur ?? '',
    margin_pct: item.margin_pct ?? '',
    internal_note: item.internal_note || '',
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await apiFetch(`/requests/${request.id}/items/${encodeURIComponent(item.key)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: form.status,
          item_partner_name: form.item_partner_name || null,
          price_net_eur: form.price_net_eur === '' ? null : Number(form.price_net_eur),
          margin_pct: form.margin_pct === '' ? null : Number(form.margin_pct),
          internal_note: form.internal_note || null,
        }),
      });
      toast.success(`Item atualizado: ${item.name}`);
      await onRefresh();
    } catch (err) {
      toast.error('Não foi possível atualizar o item', { description: String(err.message) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`admin-cart-item-${item.key}`} className="rounded-lg bg-white px-3 py-2.5">
      <p className="text-xs font-semibold text-eco-ink">
        <span className="mr-1.5 rounded-full bg-eco-green/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-eco-green">{item.type}</span>
        {item.name}
        {item.destination && <span className="font-normal text-eco-ink/50"> · {item.destination}</span>}
        {item.qty ? <span className="font-normal text-eco-ink/50"> · ×{item.qty}</span> : null}
        {item.dates && <span className="font-normal text-eco-ink/50"> · {item.dates}</span>}
        {item.passengers && <span className="font-normal text-eco-ink/50"> · {item.passengers} pax</span>}
        {item.notes && <span className="block font-normal italic text-eco-ink/45">“{item.notes}”</span>}
      </p>
      <div className="mt-2 grid gap-2 md:grid-cols-5">
        <select data-testid={`item-status-${item.key}`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={selectCls}>
          {ITEM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid={`item-partner-${item.key}`} value={form.item_partner_name} onChange={(e) => setForm({ ...form, item_partner_name: e.target.value })} className={selectCls}>
          <option value="">Parceiro…</option>
          {partners.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
        </select>
        <input data-testid={`item-net-${item.key}`} type="number" min="0" step="0.01" value={form.price_net_eur} onChange={(e) => setForm({ ...form, price_net_eur: e.target.value })} placeholder="NET EUR" className={inputCls} />
        <input data-testid={`item-margin-${item.key}`} type="number" min="0" max="80" step="0.1" value={form.margin_pct} onChange={(e) => setForm({ ...form, margin_pct: e.target.value })} placeholder="Margem %" className={inputCls} />
        <button type="button" data-testid={`item-save-${item.key}`} onClick={save} disabled={busy} className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg bg-eco-green px-3 text-xs font-semibold text-white transition-colors hover:bg-eco-deep disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Guardar item
        </button>
      </div>
      <input data-testid={`item-note-${item.key}`} value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} placeholder="Nota interna (nunca pública)" className={`${inputCls} mt-2`} />
    </div>
  );
};

export const AdminRequests = ({ requests, partners, vouchers, proposals, onRefresh }) => {
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState('');
  const [currencies, setCurrencies] = useState(['EUR', 'USD', 'GBP']);

  useEffect(() => {
    apiFetch('/settings').then((s) => setCurrencies(Object.keys(s.rates || { EUR: 1 }))).catch(() => {});
  }, []);

  const voucherFor = (requestId) => vouchers.find((v) => v.request_id === requestId);
  const versionsFor = (requestId) => proposals.filter((p) => p.request_id === requestId).sort((a, b) => (b.version || 1) - (a.version || 1));
  const activeProposalFor = (requestId) => proposals.find((p) => p.request_id === requestId && ['sent', 'changes_requested', 'accepted'].includes(p.status));
  const setProposal = (id, key, value) => setForms((f) => ({ ...f, [id]: { ...emptyProposal, ...f[id], [key]: value } }));

  const updateRequest = async (id, updates) => {
    setBusy(`${id}-request`);
    try {
      await apiFetch(`/requests/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
      await onRefresh();
    } catch (err) {
      toast.error('Não foi possível atualizar o pedido', { description: String(err.message) });
    } finally {
      setBusy('');
    }
  };

  const sendProposal = async (request) => {
    const form = { ...emptyProposal, ...forms[request.id] };
    if (!form.price_eur) return toast.error('Indique o valor net do parceiro em EUR');
    setBusy(`${request.id}-proposal`);
    try {
      const accommodation = form.acc_property ? { property_name: form.acc_property, room_type: form.acc_room, nights: form.acc_nights, meal_basis: form.acc_meal } : null;
      await apiFetch(`/requests/${request.id}/proposal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          price_eur: Number(form.price_eur),
          margin_pct: form.margin_pct === '' ? null : Number(form.margin_pct),
          services: form.services,
          message: form.message,
          valid_until: form.valid_until,
          travel_dates: form.travel_dates,
          travellers: form.travellers,
          customer_currency: form.customer_currency,
          deposit_pct: form.deposit_pct === '' ? null : Number(form.deposit_pct),
          payment_deadline: form.payment_deadline,
          itinerary: parseLines(form.itinerary, ['day', 'title', 'description']),
          experiences: parseLines(form.experiences, ['name', 'date', 'duration', 'partner']),
          transfers: parseLines(form.transfers, ['route', 'vehicle', 'pickup']),
          included: form.included.split('\n').map((s) => s.trim()).filter(Boolean),
          not_included: form.not_included.split('\n').map((s) => s.trim()).filter(Boolean),
          accommodation,
          conditions: { cancellation: form.cond_cancellation, modification: form.cond_modification, important_info: form.cond_important },
        }),
      });
      toast.success('Proposta enviada ao cliente');
      setForms((f) => ({ ...f, [request.id]: emptyProposal }));
      await onRefresh();
    } catch (err) {
      toast.error('Proposta não enviada', { description: String(err.message) });
    } finally {
      setBusy('');
    }
  };

  const generateVoucher = async (request) => {
    setBusy(`${request.id}-voucher`);
    try {
      const voucher = await apiFetch(`/requests/${request.id}/voucher`, { method: 'POST' });
      toast.success(`Voucher ${voucher.code} emitido`);
      await onRefresh();
    } catch (err) {
      toast.error('Não foi possível emitir o voucher', { description: String(err.message) });
    } finally {
      setBusy('');
    }
  };

  const completeTrip = async (request) => {
    setBusy(`${request.id}-complete`);
    try {
      await apiFetch(`/requests/${request.id}/complete`, { method: 'POST' });
      toast.success('Viagem concluída; pedido de review processado conforme automação');
      await onRefresh();
    } catch (err) {
      toast.error('Não foi possível concluir a viagem', { description: String(err.message) });
    } finally {
      setBusy('');
    }
  };

  const prefillFromVersion = (request, v) => {
    const acc = v.accommodation || {};
    const cond = v.conditions || {};
    const lines = (items, keys) => (items || []).map((i) => keys.map((k) => i[k] || '').join(' | ')).join('\n');
    setForms((f) => ({
      ...f,
      [request.id]: {
        price_eur: v.price_net_eur ?? '', margin_pct: v.margin_pct ?? '', services: v.services || '', message: '',
        valid_until: v.valid_until || '', travel_dates: v.travel_dates || '', travellers: v.travellers || '',
        customer_currency: v.customer_currency || 'EUR', deposit_pct: v.deposit_pct ?? '', payment_deadline: v.payment_deadline || '',
        itinerary: lines(v.itinerary, ['day', 'title', 'description']),
        experiences: lines(v.experiences, ['name', 'date', 'duration', 'partner']),
        transfers: lines(v.transfers, ['route', 'vehicle', 'pickup']),
        included: (v.included || []).join('\n'), not_included: (v.not_included || []).join('\n'),
        acc_property: acc.property_name || '', acc_room: acc.room_type || '', acc_nights: acc.nights || '', acc_meal: acc.meal_basis || '',
        cond_cancellation: cond.cancellation || '', cond_modification: cond.modification || '', cond_important: cond.important_info || '',
      },
    }));
    toast.success(`V${v.version || 1} copiada para o editor`);
  };

  const applyCosting = (request, draft) => {
    const selectedLines = (draft.lines || []).filter((line) => line.show_to_client);
    const selectedText = selectedLines.map((line) => `${line.client_label} — ${formatEur(line.client_price_eur)}`).join('\n');
    const clientValuesMarker = '— Valores selecionados para apresentação —';
    setForms((currentForms) => {
      const current = { ...emptyProposal, ...currentForms[request.id] };
      const baseServices = (current.services || '').split(clientValuesMarker)[0].trimEnd();
      const selectedBlock = selectedText ? `${clientValuesMarker}\n${selectedText}` : '';
      return {
        ...currentForms,
        [request.id]: {
          ...current,
          price_eur: draft.totals?.net_eur ?? '',
          margin_pct: draft.margin_pct ?? '',
          services: selectedBlock ? [baseServices, selectedBlock].filter(Boolean).join('\n') : baseServices,
        },
      };
    });
  };


  return (
    <div data-testid="admin-request-list" className="mt-8 space-y-4">
      {requests.map((r) => {
        const voucher = voucherFor(r.id);
        const versions = versionsFor(r.id);
        const proposal = activeProposalFor(r.id);
        const form = { ...emptyProposal, ...forms[r.id] };
        const canPropose = ['available', 'alternative_proposed', 'partner_confirmed', 'changes_requested'].includes(r.status) && !['sent', 'accepted'].includes(proposal?.status);
        const computedNet = Number(form.price_eur) || 0;
        const computedMargin = form.margin_pct === '' ? null : Number(form.margin_pct);
        const computedPublic = computedNet > 0 && computedMargin !== null ? computedNet * (1 + computedMargin / 100) : null;
        return (
          <article key={r.id} data-testid={`admin-request-${r.id}`} className="rounded-xl border border-eco-ink/10 bg-white p-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-eco-ink"><span className="text-eco-green">{r.reference}</span> {r.name} <span className="font-normal text-eco-ink/50">· {r.email}</span></p>
                <p className="mt-0.5 text-xs text-eco-ink/55">{r.kind}{r.destination && ` · ${r.destination}`}{r.product && ` · ${r.product}`}{r.experience && ` · ${r.experience}`}{r.package_name && ` · ${r.package_name}`}{r.travelers && ` · ${r.travelers}`}{(r.travel_date || r.dates) && ` · ${r.travel_date || r.dates}`}{r.alternative_date && ` · alt: ${r.alternative_date}`}</p>
                {r.request_text && <p className="mt-1 text-xs font-medium text-eco-ink/65">Pedido: {r.request_text}</p>}
                {r.interests?.length > 0 && <p className="mt-1 text-xs text-eco-ink/45">Interesses: {r.interests.join(', ')}</p>}
                {(r.flight_assistance || r.departure_country || r.departure_airport) && (
                  <p className="mt-1 text-xs text-eco-ink/45">
                    Voos: {[r.departure_country, r.departure_airport, r.cabin_preference].filter(Boolean).join(' · ')}
                    {r.return_date && ` · regresso ${r.return_date}`}
                    {r.flexible_dates && ' · datas flexíveis'}
                    {r.inter_island_flight_assistance && ' · São Tomé ↔ Príncipe'}
                  </p>
                )}
                {r.accommodation_preferences?.length > 0 && <p className="mt-1 text-xs text-eco-ink/45">Alojamento: {r.accommodation_preferences.join(', ')}</p>}
                {r.transport_preferences?.length > 0 && <p className="mt-1 text-xs text-eco-ink/45">Mobilidade: {r.transport_preferences.join(', ')}</p>}
                {r.preferred_language && <p className="mt-1 text-xs text-eco-ink/45">Idioma: {r.preferred_language}</p>}
                {r.message && <p className="mt-1 text-xs italic text-eco-ink/45">“{r.message}”</p>}
                {r.cart_items?.length > 0 && (
                  <div data-testid={`admin-cart-items-${r.id}`} className="mt-3 rounded-xl border border-eco-gold/25 bg-eco-gold/5 p-3">
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-eco-gold">
                      Pedido combinado · {r.cart_items.length} serviços{r.cart_currency ? ` · moeda do cliente: ${r.cart_currency}` : ''}
                    </p>
                    <div className="space-y-2">
                      {r.cart_items.map((item) => <CartItemRow key={item.key} request={r} item={item} partners={partners} onRefresh={onRefresh} />)}
                    </div>
                  </div>
                )}
                {r.partner_response && (
                  <div data-testid={`admin-partner-response-${r.id}`} className="mt-2 rounded-lg bg-eco-green/5 px-3 py-2 text-xs text-eco-ink/65">
                    <strong>Resposta do parceiro:</strong> {r.partner_response.action}
                    {r.partner_response.supplier_price != null && <> · custo fornecedor <span className="eco-number font-semibold">{r.partner_response.supplier_price} {r.partner_response.supplier_currency}</span></>}
                    {r.partner_response.available_time && ` · hora ${r.partner_response.available_time}`}
                    {r.partner_response.capacity && ` · capacidade ${r.partner_response.capacity}`}
                    {r.partner_response.meeting_point && ` · encontro: ${r.partner_response.meeting_point}`}
                    {r.partner_response.alternative_dates && ` · alt: ${r.partner_response.alternative_dates}`}
                    {r.partner_response.operational_notes && <span className="block mt-0.5">Notas operacionais: {r.partner_response.operational_notes}</span>}
                    {r.partner_response.internal_notes && <span className="block mt-0.5 italic">Notas internas: {r.partner_response.internal_notes}</span>}
                  </div>
                )}
                {r.response_deadline && ['request_sent', 'waiting_partner'].includes(r.status) && (
                  <p className={`mt-1 text-xs font-semibold ${new Date(r.response_deadline) < new Date() ? 'text-red-500' : 'text-eco-gold'}`} data-testid={`admin-request-deadline-${r.id}`}>
                    Deadline parceiro: {new Date(r.response_deadline).toLocaleString('pt-PT')}{new Date(r.response_deadline) < new Date() ? ' — EM ATRASO' : ''}
                  </p>
                )}
              </div>
              <span data-testid={`admin-request-status-${r.id}`} className={`rounded-full px-3 py-1 text-[11px] font-bold ${statusBadge(r.status)}`}>{requestStatusLabel(r.status)}</span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-eco-ink/10 pt-4">
              <select data-testid={`admin-request-partner-${r.id}`} value={r.partner_name || ''} onChange={(e) => updateRequest(r.id, { partner_name: e.target.value })} className={selectCls}>
                <option value="">Atribuir parceiro…</option>
                {partners.map((p) => <option key={p.id} value={p.name}>{p.name} ({p.island})</option>)}
              </select>
              <select data-testid={`admin-request-status-select-${r.id}`} value={STATUSES.includes(r.status) ? r.status : 'request_sent'} onChange={(e) => updateRequest(r.id, { status: e.target.value })} className={selectCls}>
                {STATUSES.map((s) => <option key={s} value={s}>{requestStatusLabel(s)}</option>)}
              </select>
              {voucher ? (
                <a href={`/vouchers/${voucher.code}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-request-voucher-${r.id}`} className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-eco-gold/20 px-4 text-xs font-semibold text-eco-deep transition-colors hover:bg-eco-gold/30"><Ticket size={13} /> {voucher.code}</a>
              ) : ['confirmed', 'payment_pending', 'voucher_issued'].includes(r.status) ? (
                <button data-testid={`admin-request-generate-voucher-${r.id}`} onClick={() => generateVoucher(r)} className="inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-eco-green px-4 text-xs font-semibold text-white transition-colors hover:bg-eco-deep"><Ticket size={13} /> Emitir voucher manual</button>
              ) : null}
              {voucher && r.status !== 'completed' && (
                <button data-testid={`admin-request-complete-${r.id}`} onClick={() => completeTrip(r)} className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-eco-green/30 px-4 text-xs font-semibold text-eco-green transition-colors hover:bg-eco-green/10"><CheckCircle2 size={13} /> Trip completed</button>
              )}
              {busy.startsWith(r.id) && <Loader2 size={15} className="animate-spin text-eco-gold" />}
            </div>

            {versions.length > 0 && (
              <div data-testid={`admin-request-proposals-${r.id}`} className="mt-4 space-y-2">
                {versions.map((v) => (
                  <div key={v.id} data-testid={`admin-proposal-version-${v.id}`} className="flex flex-wrap items-center gap-3 rounded-xl bg-eco-cream px-4 py-3 text-xs text-eco-ink/65">
                    <span className={`rounded-full px-2.5 py-0.5 font-bold ${statusBadge(v.status)}`}>{v.status}</span>
                    <strong className="eco-number text-eco-deep">{v.proposal_ref || '—'} · V{v.version || 1}</strong>
                    <span><strong className="eco-number text-eco-green">{formatEur(v.price_public_eur)}</strong> · net <span className="eco-number">{formatEur(v.price_net_eur)}</span> · margem <span className="eco-number">{formatEur(v.commission_eur)}</span> ({v.margin_pct}%) · {v.customer_currency || 'EUR'} · câmbio {v.rates_snapshot?.captured_at ? new Date(v.rates_snapshot.captured_at).toLocaleDateString('pt-PT') : 'snapshot'}</span>
                    {v.change_request && <span className="text-amber-700">Alterações: {(v.change_request.change_types || []).join(', ')} {v.change_request.note}</span>}
                    {v.accepted_snapshot && <span className="font-semibold text-eco-green">Aceite V{v.accepted_snapshot.version} em {new Date(v.accepted_snapshot.accepted_at).toLocaleDateString('pt-PT')}</span>}
                    <button type="button" onClick={async () => { try { await downloadAuthedFile(`/proposals/${v.id}/pdf`, `${v.proposal_ref || 'proposal'}.pdf`); } catch { toast.error('PDF indisponível'); } }} data-testid={`admin-proposal-pdf-${v.id}`} className="font-semibold text-eco-gold">PDF</button>
                  </div>
                ))}
              </div>
            )}

            {canPropose && (
              <div data-testid={`admin-proposal-form-${r.id}`} className="mt-4 rounded-xl border border-eco-gold/25 bg-eco-gold/5 p-4">
                <div className="grid gap-3 md:grid-cols-5">
                  <input data-testid={`proposal-net-${r.id}`} type="number" min="1" step="0.01" value={form.price_eur} onChange={(e) => setProposal(r.id, 'price_eur', e.target.value)} placeholder="Net partner EUR (supplier cost)" className={inputCls} />
                  <input data-testid={`proposal-margin-${r.id}`} type="number" min="0" max="80" step="0.1" value={form.margin_pct} onChange={(e) => setProposal(r.id, 'margin_pct', e.target.value)} placeholder="Margem %" className={inputCls} />
                  <select data-testid={`proposal-currency-${r.id}`} value={form.customer_currency} onChange={(e) => setProposal(r.id, 'customer_currency', e.target.value)} className={inputCls}>
                    {currencies.map((c) => <option key={c} value={c}>Cliente: {c}</option>)}
                  </select>
                  <input data-testid={`proposal-deposit-${r.id}`} type="number" min="1" max="99" step="1" value={form.deposit_pct} onChange={(e) => setProposal(r.id, 'deposit_pct', e.target.value)} placeholder="Sinal % (opcional)" className={inputCls} />
                  <input data-testid={`proposal-valid-${r.id}`} value={form.valid_until} onChange={(e) => setProposal(r.id, 'valid_until', e.target.value)} placeholder="Válida até" className={inputCls} />
                </div>
                {computedPublic !== null && (
                  <p data-testid={`proposal-summary-${r.id}`} className="eco-number mt-3 rounded-lg bg-white px-3 py-2 text-xs text-eco-ink/70">
                    Supplier cost <strong>{formatEur(computedNet)}</strong> · Margem Eco <strong className="text-eco-gold">{formatEur(computedPublic - computedNet)}</strong> ({computedMargin}%) · Preço cliente <strong className="text-eco-green">{formatEur(computedPublic)}</strong> · Partner amount <strong>{formatEur(computedNet)}</strong> · Gross margin <strong className="text-eco-gold">{computedPublic > 0 ? (((computedPublic - computedNet) / computedPublic) * 100).toFixed(1) : 0}%</strong>
                  </p>
                )}
                <AdminProposalCalculator request={r} partners={partners} onApply={(draft) => applyCosting(r, draft)} />
                <details className="mt-3">
                  <summary data-testid={`proposal-full-toggle-${r.id}`} className="cursor-pointer text-xs font-semibold text-eco-ink/60">Proposta completa (itinerário, alojamento, condições)</summary>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <input data-testid={`proposal-travel-dates-${r.id}`} value={form.travel_dates} onChange={(e) => setProposal(r.id, 'travel_dates', e.target.value)} placeholder="Datas da viagem (ex: 12–17 Outubro 2026)" className={inputCls} />
                    <input data-testid={`proposal-travellers-${r.id}`} value={form.travellers} onChange={(e) => setProposal(r.id, 'travellers', e.target.value)} placeholder="Viajantes (ex: 2 adultos)" className={inputCls} />
                    <input data-testid={`proposal-paydeadline-${r.id}`} value={form.payment_deadline} onChange={(e) => setProposal(r.id, 'payment_deadline', e.target.value)} placeholder="Limite de pagamento" className={inputCls} />
                    <input data-testid={`proposal-services-${r.id}`} value={form.services} onChange={(e) => setProposal(r.id, 'services', e.target.value)} placeholder="Serviços incluídos (resumo)" className={inputCls} />
                    <textarea data-testid={`proposal-itinerary-${r.id}`} rows={3} value={form.itinerary} onChange={(e) => setProposal(r.id, 'itinerary', e.target.value)} placeholder={'Itinerário — uma linha por dia:\nDay 1 | Chegada & Transfer privado | Recolha no aeroporto\nDay 2 | Experiência local'} className={inputCls} />
                    <textarea data-testid={`proposal-experiences-${r.id}`} rows={3} value={form.experiences} onChange={(e) => setProposal(r.id, 'experiences', e.target.value)} placeholder={'Experiências — uma por linha:\nRota do Cacau | 13 Out | Dia inteiro | Navetur Equatur'} className={inputCls} />
                    <textarea data-testid={`proposal-transfers-${r.id}`} rows={3} value={form.transfers} onChange={(e) => setProposal(r.id, 'transfers', e.target.value)} placeholder={'Transfers — um por linha:\nAeroporto → Hotel | SUV | Recolha nas chegadas'} className={inputCls} />
                    <textarea data-testid={`proposal-included-${r.id}`} rows={3} value={form.included} onChange={(e) => setProposal(r.id, 'included', e.target.value)} placeholder={'Incluído — um por linha:\nTransfers privados\nGuia local'} className={inputCls} />
                    <textarea data-testid={`proposal-notincluded-${r.id}`} rows={3} value={form.not_included} onChange={(e) => setProposal(r.id, 'not_included', e.target.value)} placeholder={'Não incluído — um por linha:\nVoos internacionais'} className={inputCls} />
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid={`proposal-acc-property-${r.id}`} value={form.acc_property} onChange={(e) => setProposal(r.id, 'acc_property', e.target.value)} placeholder="Alojamento: propriedade" className={inputCls} />
                      <input data-testid={`proposal-acc-room-${r.id}`} value={form.acc_room} onChange={(e) => setProposal(r.id, 'acc_room', e.target.value)} placeholder="Tipo de quarto" className={inputCls} />
                      <input data-testid={`proposal-acc-nights-${r.id}`} value={form.acc_nights} onChange={(e) => setProposal(r.id, 'acc_nights', e.target.value)} placeholder="Noites" className={inputCls} />
                      <input data-testid={`proposal-acc-meal-${r.id}`} value={form.acc_meal} onChange={(e) => setProposal(r.id, 'acc_meal', e.target.value)} placeholder="Regime (BB, HB…)" className={inputCls} />
                    </div>
                    <div className="grid gap-3">
                      <input data-testid={`proposal-cond-cancel-${r.id}`} value={form.cond_cancellation} onChange={(e) => setProposal(r.id, 'cond_cancellation', e.target.value)} placeholder="Condições de cancelamento" className={inputCls} />
                      <input data-testid={`proposal-cond-mod-${r.id}`} value={form.cond_modification} onChange={(e) => setProposal(r.id, 'cond_modification', e.target.value)} placeholder="Condições de modificação" className={inputCls} />
                      <input data-testid={`proposal-cond-important-${r.id}`} value={form.cond_important} onChange={(e) => setProposal(r.id, 'cond_important', e.target.value)} placeholder="Informação importante de reserva" className={inputCls} />
                    </div>
                    <textarea data-testid={`proposal-message-${r.id}`} rows={2} value={form.message} onChange={(e) => setProposal(r.id, 'message', e.target.value)} placeholder="Mensagem ao cliente" className={inputCls} />
                  </div>
                </details>
                <button data-testid={`proposal-send-${r.id}`} onClick={() => sendProposal(r)} disabled={busy === `${r.id}-proposal`} className="eco-btn-primary mt-4 justify-center disabled:opacity-60"><Send size={13} /> {versions.length > 0 ? `Enviar proposta V${versions.length + 1}` : 'Enviar proposta V1'}</button>
                {versions.length > 0 && (
                  <button type="button" data-testid={`proposal-prefill-${r.id}`} onClick={() => prefillFromVersion(r, versions[0])} className="eco-btn-secondary mt-4 ml-2 justify-center"><Copy size={13} /> Copiar V{versions[0].version || 1} para o editor</button>
                )}
              </div>
            )}

            {r.history?.length > 0 && (
              <details className="mt-4 text-xs text-eco-ink/50">
                <summary data-testid={`admin-history-toggle-${r.id}`} className="cursor-pointer font-semibold text-eco-ink/60">Histórico operacional ({r.history.length})</summary>
                <div className="mt-2 space-y-1.5">
                  {r.history.slice().reverse().map((h, i) => <p key={`${h.at}-${i}`}>{new Date(h.at).toLocaleString('pt-PT')} · {requestStatusLabel(h.status)}{h.note && ` — ${h.note}`}</p>)}
                </div>
              </details>
            )}
          </article>
        );
      })}
      {requests.length === 0 && <p className="rounded-xl border border-dashed border-eco-ink/15 bg-white py-12 text-center text-sm text-eco-ink/50">Ainda não há pedidos.</p>}
    </div>
  );
};
